import { IOrdersHook } from '@models';
import { OrderService } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useOrders = (): IOrdersHook => {
  const { orderStore } = useStore();
  const { orders, totalCount, currentPage, skipCount, maxResultCount, loading, ordersError, handlePageChange } =
    orderStore;
  const [service, setService] = useState<string>();
  const [customer, setCustomer] = useState<string>();
  const [registrationNumber, setRegistrationNumber] = useState<string>();
  const [status, setStatus] = useState<string[] | undefined>();
  const [dateRange, setDateRange] = useState<string | undefined>();
  const [createdRange, setCreatedRange] = useState<string | undefined>();
  const [scheduledFrom, setScheduledFrom] = useState<string | undefined>();
  const [scheduledTo, setScheduledTo] = useState<string | undefined>();
  const [createdCustomFrom, setCreatedCustomFrom] = useState<string | undefined>();
  const [createdCustomTo, setCreatedCustomTo] = useState<string | undefined>();
  // ABP sorting expression from the table's date columns, e.g. 'StartAt DESC'.
  const [sorting, setSorting] = useState<string | undefined>();

  const statusParam = useMemo(() => (status?.length ? status.join(',') : undefined), [status]);

  const { startAt, endAt } = useMemo(
    () =>
      dateRange === 'custom'
        ? { startAt: scheduledFrom, endAt: scheduledTo }
        : getDateRangeFromPreset(dateRange),
    [dateRange, scheduledFrom, scheduledTo]
  );
  const { startAt: createdFrom, endAt: createdTo } = useMemo(
    () =>
      createdRange === 'custom'
        ? { startAt: createdCustomFrom, endAt: createdCustomTo }
        : getDateRangeFromPreset(createdRange),
    [createdRange, createdCustomFrom, createdCustomTo]
  );

  useEffect(() => {
    return () => orderStore.resetOrders();
  }, [orderStore]);

  // The request the table is showing, kept whole so that retrying after a failure asks for
  // exactly what failed rather than for the defaults.
  const request = useMemo(
    () => ({
      registrationNumber,
      status: statusParam,
      service,
      customer,
      startAt,
      endAt,
      createdFrom,
      createdTo,
      maxResultCount,
      skipCount,
      sorting,
    }),
    [registrationNumber, statusParam, service, customer, startAt, endAt, createdFrom, createdTo, maxResultCount, skipCount, sorting]
  );

  useEffect(() => {
    orderStore.getAll(request);
  }, [orderStore, request]);

  const handleRetry = useCallback(() => orderStore.getAll(request), [orderStore, request]);

  const handleSortChange = (value?: string) => {
    setSorting(value);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleServiceSearch = debounce((text: string) => {
    setService(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleCustomerSearch = debounce((text: string) => {
    setCustomer(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleRegistrationNumberSearch = debounce((text: string) => {
    setRegistrationNumber(text);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  }, 500);

  const handleStatusChange = (values: string[]) => {
    setStatus(values?.length ? values : undefined);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleDateRangeChange = (range: string) => {
    setDateRange(range || undefined);
    if (range !== 'custom') {
      setScheduledFrom(undefined);
      setScheduledTo(undefined);
    }
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleScheduledCustomRange = (from?: string, to?: string) => {
    setScheduledFrom(from);
    setScheduledTo(to);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleCreatedRangeChange = (range: string) => {
    setCreatedRange(range || undefined);
    if (range !== 'custom') {
      setCreatedCustomFrom(undefined);
      setCreatedCustomTo(undefined);
    }
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const handleCreatedCustomRange = (from?: string, to?: string) => {
    setCreatedCustomFrom(from);
    setCreatedCustomTo(to);
    orderStore.setSkipCount(0);
    orderStore.setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const result = await OrderService.getAll({
      registrationNumber,
      status: statusParam,
      service,
      customer,
      startAt,
      endAt,
      createdFrom,
      createdTo,
      maxResultCount: totalCount || 10000,
      skipCount: 0,
      sorting,
    });
    return result.items;
  }, [registrationNumber, statusParam, service, customer, startAt, endAt, createdFrom, createdTo, totalCount, sorting]);

  return {
    loading,
    error: ordersError,
    status,
    dateRange,
    createdRange,
    scheduledFrom,
    scheduledTo,
    createdCustomFrom,
    createdCustomTo,
    dataSource: Array.from(orders.values()),
    totalCount,
    skipCount,
    currentPage,
    maxResultCount,
    handleServiceSearch,
    handleCustomerSearch,
    handleRegistrationNumberSearch,
    handleStatusChange,
    handleDateRangeChange,
    handleScheduledCustomRange,
    handleCreatedRangeChange,
    handleCreatedCustomRange,
    handlePageChange,
    handleSortChange,
    handleRetry,
    sorting,
    fetchAllForExport,
  };
};
